// src/components/league-dashboard/LeagueBanner.js
import React from 'react';
import { dynastyTheme } from '../../services/colorService';

/**
 * Reusable League Banner Component
 * Displays league banner image with overlaid title and optional subtitle/actions
 */
const LeagueBanner = ({
  leagueBanner,
  title,
  subtitle,
  badge,
  actions,
  centerContent,
  minHeight = '120px',
  bannerConfig = null
}) => {
  // Use bannerConfig if provided, otherwise use minHeight prop
  const height = bannerConfig ? `${bannerConfig.height}px` : minHeight;
  const titleSize = bannerConfig?.titleSize || 'text-3xl';
  const titlePosition = bannerConfig?.titlePosition || 'left';
  const titleStyle = bannerConfig?.titleStyle || 'font-bold';
  
  const getTextAlign = (position) => {
    switch(position) {
      case 'center': return 'center';
      case 'right': return 'right';
      default: return 'left';
    }
  };

  return (
    <div
      className={`${dynastyTheme.components.card.highlighted} relative`}
      style={{ minHeight: height }}
    >
      {/* League Banner Background */}
      {leagueBanner && (
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={leagueBanner}
            alt="League Banner"
            className="w-full h-full object-cover opacity-90"
            style={{
              filter: 'contrast(1.1) brightness(1.1)'
            }}
            onError={(e) => {
              console.error('Failed to load league banner');
              e.target.style.display = 'none';
            }}
          />
          {/* Gradient overlays for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/20 via-transparent to-neutral-900/70"></div>
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 40%, rgba(28,25,23,0.4) 100%)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/50 via-transparent to-neutral-900/50"></div>
        </div>
      )}

      {/* Content over banner with text shadows for readability */}
      <div className="relative py-6 px-6 z-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-shrink-0" style={{ flex: titlePosition === 'center' ? '1' : 'none', textAlign: getTextAlign(titlePosition) }}>
            {/* Title with strong shadow for readability and custom styling */}
            <h1
              className={`${titleSize} ${titleStyle} ${dynastyTheme.classes.text.white} mb-1`}
              style={{
                textShadow: leagueBanner
                  ? '2px 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)'
                  : 'none'
              }}
            >
              {title}
            </h1>
            
            {/* Subtitle if provided */}
            {subtitle && (
              <p
                className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}
                style={{
                  textShadow: leagueBanner
                    ? '1px 1px 6px rgba(0,0,0,0.9), 0 0 15px rgba(0,0,0,0.7)'
                    : 'none'
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          {/* Center content (e.g., commissioner controls) */}
          {centerContent && (
            <div className="flex items-center">
              {centerContent}
            </div>
          )}

          {/* Badge/Actions on right side */}
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {badge}
            {actions}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueBanner;
